import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { useStorage } from "@/hooks/useStorage";
import type { ApplyFormData } from "@/types/contexts/apply";

const STORAGE_KEYS = {
  FORM_DATA: "@apply_form_data",
  LAST_STEP: "@apply_last_step",
} as const;

const initialFormData: ApplyFormData = {
  // Eligibility
  authorizedToWork: null,
  over18: null,

  // Info
  previouslyWorked: null,
  firstName: "",
  middleInitial: "",
  lastName: "",
  preferredName: "",
  email: "",
  phone: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  state: "",
  zip: "",
  office: "",
  referralSource: "",
  referralName: "",

  // Physical - 11 requirements
  physicalRequirements: Array(11).fill(false),

  // Additional
  heatInjury: null,
  hasTransportation: null,
  eventServicesExperience: null,
  hasCellPhone: null,
  hasAdditionalInfo: null,
  additionalInfo: "",
};

type ApplyContextType = {
  formData: ApplyFormData;
  updateFormData: (updates: Partial<ApplyFormData>) => void;
  resetFormData: () => void;
  currentStep: number;
  updateStep: (step: number) => void;
  clearSavedData: () => void;
  hasSavedData: boolean;
  isLoading: boolean;
};

const ApplyContext = createContext<ApplyContextType | undefined>(undefined);

export function ApplyProvider({ children }: { children: ReactNode }) {
  const storage = useStorage();
  const [formData, setFormData] = useState<ApplyFormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved data on mount
  useEffect(() => {
    try {
      const savedFormData = storage.getString(STORAGE_KEYS.FORM_DATA);
      const savedStep = storage.getNumber(STORAGE_KEYS.LAST_STEP);

      if (savedFormData) {
        const parsed = JSON.parse(savedFormData) as ApplyFormData;
        setFormData(parsed);
        setHasSavedData(true);
      }

      if (savedStep !== undefined) {
        setCurrentStep(savedStep);
      }
    } catch (error) {
      console.error("Error loading saved application data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [storage]);

  // Debounced save to storage
  const saveToStorage = useCallback(
    (data: ApplyFormData, step: number) => {
      // Only save if user has progressed past eligibility step
      if (step === 0) {
        return;
      }

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        try {
          storage.saveFast(STORAGE_KEYS.FORM_DATA, JSON.stringify(data));
          storage.saveFast(STORAGE_KEYS.LAST_STEP, step);
          setHasSavedData(true);
        } catch (error) {
          console.error("Error saving application data:", error);
        }
      }, 500);
    },
    [storage],
  );

  const updateFormData = useCallback(
    (updates: Partial<ApplyFormData>) => {
      setFormData((prev) => {
        const next = { ...prev, ...updates };
        saveToStorage(next, currentStep);
        return next;
      });
    },
    [currentStep, saveToStorage],
  );

  const updateStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      saveToStorage(formData, step);
    },
    [formData, saveToStorage],
  );

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
    // Note: We don't clear storage here - only clearSavedData does that
  }, []);

  const clearSavedData = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    storage.removeItem(STORAGE_KEYS.FORM_DATA);
    storage.removeItem(STORAGE_KEYS.LAST_STEP);
    setFormData(initialFormData);
    setCurrentStep(0);
    setHasSavedData(false);
  }, [storage]);

  return (
    <ApplyContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData,
        currentStep,
        updateStep,
        clearSavedData,
        hasSavedData,
        isLoading,
      }}
    >
      {children}
    </ApplyContext.Provider>
  );
}

export function useApply() {
  const context = useContext(ApplyContext);
  if (context === undefined) {
    throw new Error("useApply must be used within an ApplyProvider");
  }
  return context;
}
